import clsx from 'clsx';
import styles from './PhotoListSidebar.module.css';
import { getStatusColor } from '../../../../utils/statusUtils';

/**
 * Left sidebar showing list of photos with filtering and sorting.
 *
 * Single responsibility: Display and filter photo list.
 * Does not contain business logic - receives all state via props.
 */
export default function PhotoListSidebar({
  photos,
  selectedImageId,
  loadingList,
  error,
  sortOrder,
  search,
  filters,
  newImageCount,
  onSelectPhoto,
  onSortChange,
  onSearchChange,
  onFilterToggle,
  onClearFilters,
  onRefresh,
  getStatusLabel,
}) {
  return (
    <div className={styles.panel}>
      <div className={styles.stickyHeader}>
        <div className={styles.headerRow}>
          <select
            className={styles.sortSelect}
            value={sortOrder}
            onChange={(e) => onSortChange(e.target.value)}
          >
            <option value="oldest">Added oldest</option>
            <option value="newest">Added newest</option>
            <option value="capture_oldest">Captured oldest</option>
            <option value="capture_newest">Captured newest</option>
            <option value="filename_asc">A-Z Filename</option>
            <option value="filename_desc">Z-A Filename</option>
          </select>
          <button
            className={styles.refreshButton}
            onClick={onRefresh}
          >
            Refresh
            {newImageCount > 0 && (
              <span className={styles.badge}>+{newImageCount}</span>
            )}
          </button>
        </div>

        <div className={styles.toolbar}>
          <input
            className={styles.searchInput}
            placeholder="Search filename or id"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <button
            className={styles.clearButton}
            onClick={onClearFilters}
            title="Clear filters"
          >
            x
          </button>
        </div>

        <div className={styles.filterGroup}>
          <FilterChip
            active={filters.includes('all')}
            onClick={() => onFilterToggle('all')}
          >
            All
          </FilterChip>
          <FilterChip
            active={filters.includes('tagging')}
            onClick={() => onFilterToggle('tagging')}
          >
            Tagging
          </FilterChip>
          <FilterChip
            active={filters.includes('review')}
            onClick={() => onFilterToggle('review')}
          >
            Review
          </FilterChip>
          <FilterChip
            active={filters.includes('attention')}
            onClick={() => onFilterToggle('attention')}
          >
            Attention
          </FilterChip>
          <FilterChip
            active={filters.includes('done')}
            onClick={() => onFilterToggle('done')}
          >
            Done
          </FilterChip>
          <select
            className={styles.moreSelect}
            value=""
            onChange={(e) => {
              if (e.target.value) {
                onFilterToggle(e.target.value);
                e.target.value = '';
              }
            }}
          >
            <option value="">More...</option>
            <option value="sent_to_editor">
              {filters.includes('sent_to_editor') ? '[x] ' : '[ ] '}To Editor
            </option>
            <option value="skipped">
              {filters.includes('skipped') ? '[x] ' : '[ ] '}Skipped
            </option>
            <option value="error">
              {filters.includes('error') ? '[x] ' : '[ ] '}Error
            </option>
          </select>
        </div>
      </div>

      {loadingList && <div className={styles.loadingLine}>Loading list...</div>}
      {error && <div className={styles.errorText}>{error}</div>}

      <div className={styles.list}>
        {photos.map((photo) => (
          <PhotoListItem
            key={photo.image_id}
            photo={photo}
            isSelected={photo.image_id === selectedImageId}
            statusColor={getStatusColor(photo)}
            statusLabel={getStatusLabel(photo)}
            onClick={() => onSelectPhoto(photo.image_id)}
          />
        ))}
        {!loadingList && photos.length === 0 && (
          <div className={styles.emptyCard}>
            <div className={styles.emptyFallback}>No images yet</div>
            <div className={styles.emptyMeta}>
              <div className={styles.emptyName}>Waiting for done items</div>
              <div className={styles.emptySub}>Only status "done" appears here.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Filter chip button component.
 */
function FilterChip({ active, onClick, children }) {
  return (
    <button
      className={clsx(styles.chip, active && styles.chipActive)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Individual photo list item.
 */
function PhotoListItem({ photo, isSelected, statusColor, statusLabel, onClick }) {
  const isExporting = photo.export_summary?.pending_count > 0;

  return (
    <div
      className={clsx(
        styles.listCard,
        isSelected && styles.listCardActive,
        isExporting && styles.exporting
      )}
      onClick={onClick}
    >
      <div className={styles.thumb}>
        {photo.preview_url ? (
          <img src={photo.preview_url} alt={photo.image_id} />
        ) : (
          <div className={styles.thumbFallback}>No preview</div>
        )}
        <span
          className={styles.statusDot}
          style={{ background: statusColor }}
        />
        {isExporting && (
          <div className={styles.exportingOverlay}>
            <div className={styles.exportingSpinner} />
          </div>
        )}
      </div>
      <div className={styles.meta}>
        <div className={styles.title}>{photo.filename || photo.image_id}</div>
        <div className={styles.sub}>
          Faces {photo.labeled_count ?? '?'}/{photo.face_count ?? '?'} - Unlabeled{' '}
          {photo.unlabeled_count ?? '?'}
        </div>
        <div className={styles.sub}>
          <span
            className={styles.pill}
            style={{ borderColor: statusColor, color: statusColor }}
          >
            {statusLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
